import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useChatSessions, useChatMessages, useChatConnection, useChatErrors } from '../../contexts/ChatContext';
import { useUnifiedSignalR } from '../../contexts/UnifiedSignalRContext';
import { useAuth } from '../../context/AuthContext';
import { useNewSessionContent } from '../../hooks/useNewSessionContent';
import { ChatMessage as ChatMessageComponent } from './ChatMessage';
import { ChatErrorList } from './ChatErrorDisplay';
import { ActiveRunsDialog } from './ActiveRunsDialog';
import { ChatMessage } from '../../entities/ChatMessage';
import { chatService } from '../../services';
import { ActiveRunsErrorResponse, ApiError } from '../../types/api';
import type { SignalRActiveRunsBlockedEvent, SignalRRunsStatusEvent } from '../../types/chat';

import { ChatInput } from './ChatInput';
import { SessionList } from './SessionList';
import { FeedbackPanel } from './FeedbackPanel';
import QuickQuestions from './QuickQuestions';
import QuickQuestionsTooltip from './QuickQuestionsTooltip';
// import VoiceButton from './VoiceButton';
import { Button } from '../ui/button';
import { MessageSquare, Loader2, ChevronDown, ChevronRight, ChevronLeft, Plus } from 'lucide-react';
import { ChatProps } from '../../types/components';
import { getStatusEmoji, getStatusTooltip } from '../../utils/statusHelpers';
import { DEFAULT_URLS, UI_TIMEOUTS, PERFORMANCE_LIMITS } from '../../config/defaults';

export const Chat: React.FC<ChatProps> = ({
  sessionId: externalSessionId,
  messages: externalMessages,
  onSendMessage: externalOnSendMessage,
  loading = false
}) => {
  // Use context hooks for chat functionality
  const { sessions, currentSession, currentSessionId, pendingNewSession, loadingSessions, hasMoreSessions, isLoadingMore, startNewSession, selectSession, deleteSession, loadMoreSessions } = useChatSessions();
  const { messages: hookMessages, loadingMessages, sendMessage: hookSendMessage, isTyping, statusMessage } = useChatMessages();
  const { isConnected } = useChatConnection();
  const { errors, removeError } = useChatErrors();

  // Access the reconnecting state for better UX
  const { isReconnecting, subscribe } = useUnifiedSignalR();

  // Get user for privacy URL
  const { user } = useAuth();

  // Fetch new session content
  const { content: newSessionContent, isLoading: isLoadingContent, error: contentError } = useNewSessionContent();

  // Active runs dialog state
  const [activeRunsDialogOpen, setActiveRunsDialogOpen] = useState(false);
  const [activeRunsError, setActiveRunsError] = useState<ActiveRunsErrorResponse | null>(null);
  const [isCancellingRuns, setIsCancellingRuns] = useState(false);
  const pendingMessageRef = useRef<string | null>(null);

  // Inline performance optimizations (previously from useChatOptimized)
  // Memoize sorted sessions to avoid expensive re-sorting on every render
  const sortedSessions = useMemo(() => {
    return [...sessions].sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [sessions]);

  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const prevIsTypingRef = useRef<boolean>(false);

  // State for expandable panels - default collapsed
  const [isSessionListExpanded, setIsSessionListExpanded] = useState(false);
  const [isFeedbackExpanded, setIsFeedbackExpanded] = useState(false);

  // Accordion behavior - only one panel can be expanded at a time
  const handlePanelToggle = useCallback((panelType: 'sessions' | 'feedback') => {
    switch (panelType) {
      case 'sessions':
        setIsSessionListExpanded(!isSessionListExpanded);
        if (!isSessionListExpanded) {
          // Collapse other panels when expanding sessions
          setIsFeedbackExpanded(false);
        }
        break;
      case 'feedback':
        setIsFeedbackExpanded(!isFeedbackExpanded);
        if (!isFeedbackExpanded) {
          // Collapse other panels when expanding feedback
          setIsSessionListExpanded(false);
        }
        break;
    }
  }, [isSessionListExpanded, isFeedbackExpanded]);

  // Refs for scrolling and focusing
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  // Use external props if provided, otherwise use hook values
  // Fix: Use currentSessionId directly instead of currentSession?.id to avoid timing issues
  const activeSessionId = externalSessionId || currentSessionId;
  const baseMessages = externalMessages || hookMessages;
  const isLoading = loading || loadingMessages;

  // Create display messages that include welcome message for new sessions
  const displayMessages = useMemo(() => {
    if (pendingNewSession && newSessionContent && !isLoadingContent) {
      // Create a welcome assistant message using the new session content
      const welcomeMessage = ChatMessage.createAssistantMessage(
        newSessionContent.message,
        'temp-session-id' // Use temp session ID for pending session
      );
      welcomeMessage.id = 'welcome-message'; // Set a predictable ID
      // console.log('Displaying welcome message for new session:', welcomeMessage);
      return [welcomeMessage, ...baseMessages];
    }
    return baseMessages;
  }, [pendingNewSession, newSessionContent, isLoadingContent, baseMessages]);

  // Use optimized values for better performance
  const optimizedSessions = externalMessages ? sessions : sortedSessions;
  const optimizedMessages = externalMessages ? displayMessages : (displayMessages.length > PERFORMANCE_LIMITS.MAX_MESSAGES_IN_MEMORY ? displayMessages.slice(-PERFORMANCE_LIMITS.MAX_MESSAGES_IN_MEMORY) : displayMessages);

  // Auto-start new session when configured or when no sessions exist and not in external mode
  useEffect(() => {
    const startWithNewSession = process.env.REACT_APP_START_WITH_NEW_SESSION === 'true';

    if (!externalSessionId && isConnected && !loadingSessions && !activeSessionId && !pendingNewSession) {
      if (startWithNewSession || sessions.length === 0) {
        // Start new session if configured to do so or if no sessions exist
        startNewSession();
      }
    }
  }, [externalSessionId, isConnected, loadingSessions, activeSessionId, pendingNewSession, sessions.length, startNewSession]);

  // Auto-scroll to bottom when messages change, session changes, or agent starts/stops typing
  useEffect(() => {
    if (messagesEndRef.current && activeSessionId) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [optimizedMessages, activeSessionId, isTyping, statusMessage]);

  // Focus input when session changes or when starting a new session
  useEffect(() => {
    if ((activeSessionId || pendingNewSession) && chatInputRef.current) {
      const timer = setTimeout(() => {
        chatInputRef.current?.focus();
      }, UI_TIMEOUTS.FOCUS_DELAY);
      return () => clearTimeout(timer);
    }
  }, [activeSessionId, pendingNewSession]);

  // Additional focus effect specifically for new session state changes
  useEffect(() => {
    if (pendingNewSession && chatInputRef.current) {
      // More immediate focus for new session
      const timer = setTimeout(() => {
        chatInputRef.current?.focus();
      }, UI_TIMEOUTS.FOCUS_DELAY_SHORT);
      return () => clearTimeout(timer);
    }
  }, [pendingNewSession]);

  // Focus input after AI finishes typing
  useEffect(() => {
    // When isTyping changes from true to false (AI finished responding)
    // Only focus if we're not in external mode (standalone chat component)
    if (prevIsTypingRef.current && !isTyping && activeSessionId && chatInputRef.current && !externalSessionId) {
      const timer = setTimeout(() => {
        chatInputRef.current?.focus();
      }, UI_TIMEOUTS.FOCUS_DELAY);
      return () => clearTimeout(timer);
    }
    // Update the previous isTyping state
    prevIsTypingRef.current = isTyping;
  }, [isTyping, activeSessionId, externalSessionId]);

  // Handle 429 active runs errors
  const handleActiveRunsError = useCallback((error: ApiError & { activeRunsData?: ActiveRunsErrorResponse }, messageContent: string) => {
    if (error.activeRunsData) {
      setActiveRunsError(error.activeRunsData);
      pendingMessageRef.current = messageContent;
      setActiveRunsDialogOpen(true);
    }
  }, []);

  // Handle user choosing to wait for current response
  const handleActiveRunsWait = useCallback(() => {
    // Restore the pending message to the input so user doesn't lose it
    if (pendingMessageRef.current) {
      setInputValue(pendingMessageRef.current);
      pendingMessageRef.current = null;
    }
    setActiveRunsDialogOpen(false);
    setActiveRunsError(null);
  }, []);

  // Handle user choosing to cancel current response
  const handleActiveRunsCancel = useCallback(async () => {
    if (!activeSessionId) return;

    setIsCancellingRuns(true);
    try {
      await chatService.cancelRuns(activeSessionId);
      // Dialog will be closed when we receive runsStatus event via SignalR
      // The handleRunsStatusEvent callback handles cleanup and message restoration
    } catch (error) {
      console.error('Failed to cancel runs:', error);
      setIsCancellingRuns(false);
      // On error, restore the pending message to input
      if (pendingMessageRef.current) {
        setInputValue(pendingMessageRef.current);
        pendingMessageRef.current = null;
      }
      setActiveRunsDialogOpen(false);
      setActiveRunsError(null);
    }
  }, [activeSessionId]);

  // Handle runsStatus SignalR event (run cancellation/completion confirmation)
  const handleRunsStatusEvent = useCallback((data: SignalRRunsStatusEvent) => {
    // Handle all status types that indicate processing is done
    if (data.type === 'runs_cancelled' || data.type === 'runs_completed' || data.type === 'runs_failed') {
      setActiveRunsDialogOpen(false);
      setActiveRunsError(null);
      setIsCancellingRuns(false);
      
      // Only retry sending the pending message if runs were cancelled
      // For completed/failed, the runs finished naturally
      if (data.type === 'runs_cancelled' && pendingMessageRef.current) {
        const message = pendingMessageRef.current;
        pendingMessageRef.current = null;
        // Use a small delay to ensure state has updated
        setTimeout(() => {
          setInputValue(message);
        }, 100);
      }
    }
  }, []);

  // Handle activeRunsBlocked SignalR event
  const handleActiveRunsBlockedEvent = useCallback((data: SignalRActiveRunsBlockedEvent) => {
    if (data.sessionId === activeSessionId) {
      setActiveRunsError({
        error: data.message,
        code: 'ACTIVE_RUNS_FOUND',
        message: data.message,
        activeRuns: data.activeRuns || [],
        canCancel: data.canCancel,
        sessionId: data.sessionId,
        actions: {
          wait: {
            description: 'Wait for the current response to complete',
            recommendation: 'Recommended for preserving conversation flow'
          },
          cancel: {
            description: 'Cancel current response and send new message',
            endpoint: `/api/chat/session/${data.sessionId}/cancel-runs`,
            method: 'POST',
            warning: 'This will interrupt the current AI response'
          }
        }
      });
      setActiveRunsDialogOpen(true);
    }
  }, [activeSessionId]);

  // Subscribe to SignalR events for active runs
  useEffect(() => {
    const unsubscribeRunsStatus = subscribe('runsStatus', handleRunsStatusEvent);
    const unsubscribeActiveRunsBlocked = subscribe('activeRunsBlocked', handleActiveRunsBlockedEvent);

    return () => {
      unsubscribeRunsStatus();
      unsubscribeActiveRunsBlocked();
    };
  }, [subscribe, handleRunsStatusEvent, handleActiveRunsBlockedEvent]);

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || !isConnected || isSending) return;

    const messageToSend = inputValue.trim();
    setIsSending(true);
    setInputValue(''); // Clear input immediately when starting to send

    try {
      if (externalOnSendMessage) {
        // Use external handler if provided
        externalOnSendMessage(messageToSend);
      } else {
        // Use internal hook method - it will handle session creation if needed
        await hookSendMessage(messageToSend);
      }

      // Focus back to input field after sending message (only in standalone mode)
      if (!externalSessionId) {
        setTimeout(() => {
          chatInputRef.current?.focus();
        }, 0);
      }
    } catch (error: any) {
      // Handle 429 active runs error
      if (error?.status === 429 && error?.type === 'active_runs') {
        handleActiveRunsError(error, messageToSend);
      } else {
        console.error('Failed to send message:', error);
      }
    } finally {
      setIsSending(false);
    }
  }, [inputValue, externalOnSendMessage, hookSendMessage, isConnected, isSending, externalSessionId, handleActiveRunsError]);

  const handleQuickChipClick = useCallback(async (chipText: string) => {
    if (!isConnected || isSending) return;

    setIsSending(true);

    try {
      if (externalOnSendMessage) {
        externalOnSendMessage(chipText);
      } else {
        // Clear quick questions when user selects any quick action
        if (currentSession && currentSession.metadata?.quickQuestions) {
          // This will be handled by the hook when the message is sent
        }

        // Use internal hook method - it will handle session creation if needed
        await hookSendMessage(chipText);
      }

      // Focus back to input field after sending quick question (only in standalone mode)
      if (!externalSessionId) {
        setTimeout(() => {
          chatInputRef.current?.focus();
        }, 0);
      }
    } catch (error: any) {
      // Handle 429 active runs error
      if (error?.status === 429 && error?.type === 'active_runs') {
        handleActiveRunsError(error, chipText);
      } else {
        console.error('Failed to send quick chip message:', error);
      }
    } finally {
      setIsSending(false);
    }
  }, [externalOnSendMessage, hookSendMessage, currentSession, isConnected, isSending, externalSessionId, handleActiveRunsError]);

  /* const handleVoiceTranscript = useCallback(async (transcript: string) => {
    if (!isConnected || isSending) return;

    setIsSending(true);

    try {
      if (externalOnSendMessage) {
        externalOnSendMessage(transcript);
      } else {
        // Clear quick questions when user sends voice message
        if (currentSession && currentSession.metadata?.quickQuestions) {
          // This will be handled by the hook when the message is sent
        }

        // Use internal hook method - it will handle session creation if needed
        await hookSendMessage(transcript);
      }

      // Focus back to input field after sending voice message (only in standalone mode)
      if (!externalSessionId) {
        setTimeout(() => {
          chatInputRef.current?.focus();
        }, 0);
      }
    } catch (error) {
      console.error('Failed to send voice message:', error);
    } finally {
      setIsSending(false);
    }
  }, [externalOnSendMessage, hookSendMessage, currentSession, isConnected, isSending, externalSessionId]); */

  const handleCreateSession = useCallback(() => {
    try {
      startNewSession();
      // Collapse all panels when starting a new chat
      setIsSessionListExpanded(false);
      setIsFeedbackExpanded(false);
      // Focus the input immediately after starting new session
      setTimeout(() => {
        chatInputRef.current?.focus();
      }, UI_TIMEOUTS.NEW_SESSION_DELAY);
    } catch (error) {
      console.error('Failed to start new session:', error);
    }
  }, [startNewSession]);

  // Centralized disabled conditions
  const isInputDisabled =  isSending; // Only disable input when not connected or sending
  const isSendDisabled = !isConnected || isTyping || isSending; // Disable sending when AI is typing

  // Scroll to bottom when viewport shrinks (keyboard opened on mobile).
  // The 'viewport-shrink' event is dispatched by useViewportHeight when
  // visualViewport.height decreases by >50px — i.e. the keyboard appeared.
  useEffect(() => {
    const scrollToBottom = () => {
      // Only scroll if the chat input is focused (user tapped on it)
      const active = document.activeElement;
      if (active?.tagName === 'TEXTAREA' || active?.tagName === 'INPUT') {
        // Wait for the next frame so the layout has settled
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        });
      }
    };

    window.addEventListener('viewport-shrink', scrollToBottom);
    return () => window.removeEventListener('viewport-shrink', scrollToBottom);
  }, []);

  // Also scroll on explicit focus tap with staggered attempts
  // to cover varying keyboard animation speeds across devices
  const handleInputFocus = useCallback(() => {
    const scroll = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Attempt at key moments during keyboard animation
    setTimeout(scroll, 300);
    setTimeout(scroll, 600);
  }, []);

  return (
    <div className="flex h-full max-h-app-viewport overflow-hidden">
      {/* Only show sidebar in standalone mode (when not using external props) */}
      {/* Hidden on mobile (< md), visible on desktop (>= md) */}
      {!externalSessionId && (
        <>
          {/* Session List Toggle Button */}
          <div className="hidden md:flex flex-shrink-0 flex-col">
            {/* New Chat Button - Only show when collapsed */}
            {!isSessionListExpanded && (
              <div className="p-2 border-r border-rm-neutral-600/20">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCreateSession}
                  disabled={pendingNewSession || isTyping || isSending}
                  title={isTyping || isSending ? "Please wait for the current response to complete" : "New Chat"}
                  className="w-8 h-8 p-0 shadow-soft rounded-card"
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => handlePanelToggle('sessions')}
              className={`rounded-none border-r border-rm-neutral-600/20 flex-col items-center justify-start pt-2 p-1 text-rm-neutral-600 hover:text-rm-primary-700 hover:bg-rm-bg-50 ${isSessionListExpanded ? 'w-12 h-full' : 'w-12 flex-1'
                }`}
            >
              {isSessionListExpanded ? (
                <div className="flex flex-col items-center space-y-2">
                  <ChevronRight className="w-4 h-4" />
                  <div className="writing-mode-vertical text-xs font-medium tracking-wide transform rotate-180" style={{ writingMode: 'vertical-lr', textOrientation: 'mixed' }}>
                    Sessions
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center space-y-2">
                  <ChevronDown className="w-4 h-4" />
                  <div className="writing-mode-vertical text-xs font-medium tracking-wide transform rotate-180" style={{ writingMode: 'vertical-lr', textOrientation: 'mixed' }}>
                    Sessions
                  </div>
                </div>
              )}
            </Button>
          </div>

          {/* Expandable Session List - Desktop Only */}
          {isSessionListExpanded && (
            <div className="hidden md:flex w-80 border-r border-rm-neutral-600/20 flex-col h-full">
              <div className="p-4 border-b border-rm-neutral-600/20 flex-shrink-0">
                <Button
                  onClick={handleCreateSession}
                  className="w-full"
                  disabled={pendingNewSession || isTyping || isSending}
                  title={isTyping || isSending ? "Please wait for the current response to complete" : undefined}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  New Chat
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto">
                <SessionList
                  sessions={optimizedSessions}
                  currentSessionId={currentSession?.id || null}
                  onSessionSelect={selectSession}
                  onDeleteSession={deleteSession}
                  disabled={isTyping || isSending}
                  disabledReason={isTyping ? "Please wait for the AI to finish responding before switching sessions" : "Please wait for your message to be sent"}
                  hasMore={hasMoreSessions}
                  onLoadMore={loadMoreSessions}
                  isLoadingMore={isLoadingMore}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Main chat area */}
      <div className="flex-1 flex h-full">
        {!activeSessionId && !pendingNewSession && (loadingSessions || !isConnected) ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>{loadingSessions ? 'Loading chat...' : isReconnecting ? 'Reconnecting...' : 'Connecting...'}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat section */}
            <div className="flex-1 flex flex-col h-full">
              {/* Chat header - DISABLED */}
              {/* 
              <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <h2 className="text-lg font-semibold">
                  {externalSessionId ? `Session ${externalSessionId}` : 
                   pendingNewSession ? 'New Chat' : 
                   (currentSession?.title || 'Chat')}
                </h2>
                <div className="flex items-center space-x-2">
                  {!isConnected && (
                    <span className="text-sm text-red-500">Disconnected</span>
                  )}
                  <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                </div>
              </div>
              */}

              {/* Error Display */}
              {errors.length > 0 && (
                <div className="px-4 py-2 border-b border-gray-200">
                  <ChatErrorList
                    errors={errors}
                    onDismiss={removeError}
                  />
                </div>
              )}

              {/* Messages area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="ml-2">Loading messages...</span>
                  </div>
                ) : optimizedMessages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    {isLoadingContent ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span className="ml-2">Loading...</span>
                      </div>
                    ) : contentError ? (
                      <div className="text-red-600 p-4 border border-red-200 rounded-lg">
                        <p>Failed to load welcome message: {contentError}</p>
                      </div>
                    ) : (
                      <p>No messages yet. Start the conversation!</p>
                    )}
                  </div>
                ) : (
                  optimizedMessages.map((message) => (
                    <ChatMessageComponent
                      key={message.id}
                      message={message}
                      currentSessionId={currentSessionId}
                    />
                  ))
                )}

                {isTyping && (
                  <div className="flex items-center space-x-2 text-gray-500">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1 items-end">
                        <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1s', transform: 'translateY(0)' }}></div>
                        <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '200ms', animationDuration: '1s', transform: 'translateY(0)' }}></div>
                        <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '400ms', animationDuration: '1s', transform: 'translateY(0)' }}></div>
                      </div>
                      {statusMessage && (
                        <span
                          className="text-sm cursor-help"
                          title={getStatusTooltip(statusMessage)}
                        >
                          <span className="flex items-center space-x-2">
                            <span className="text-2xl leading-none" role="img" aria-label={statusMessage}>{getStatusEmoji(statusMessage)}</span>
                            {process.env.REACT_APP_IS_LOCAL === 'true' && (
                              <span className="ml-1 text-sm">({statusMessage})</span>
                            )}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Invisible div for auto-scrolling to bottom */}
                <div ref={messagesEndRef} />
              </div>

              {/* Input area */}
              <div className="p-2 sm:p-3 md:p-4 border-t border-gray-200 flex-shrink-0">
                {/* Show predefined quick question for new sessions */}
                {pendingNewSession && !isTyping && (
                  <div className="mb-4">
                    <QuickQuestions
                      questions={[]}
                      onQuestionClick={handleQuickChipClick}
                      disabled={isSendDisabled}
                      isVisible={!isTyping}
                    />
                  </div>
                )}

                <div className="flex space-x-1 sm:space-x-2 items-end">
                  {/* Quick Questions Lamp Icon - Always visible */}
                  {activeSessionId && currentSession && (
                    <QuickQuestionsTooltip
                      questions={
                        currentSession.metadata?.quickQuestions &&
                          currentSession.metadata.quickQuestions.length > 0 &&
                          !isTyping &&
                          !currentSession.isProcessing
                          ? [
                            ...currentSession.metadata.quickQuestions,
                            "I have another topic or question to discuss",
                            "Let's check my current wellbeing status"
                          ]
                          : []
                      }
                      onQuestionClick={handleQuickChipClick}
                      disabled={
                        isSendDisabled ||
                        currentSession.isProcessing ||
                        !currentSession.metadata?.quickQuestions ||
                        currentSession.metadata.quickQuestions.length === 0
                      }
                    />
                  )}

                  <div className="flex-1">
                    <ChatInput
                      ref={chatInputRef}
                      value={inputValue}
                      onChange={setInputValue}
                      onSend={handleSendMessage}
                      onFocus={handleInputFocus}
                      disabled={isInputDisabled}
                      sendDisabled={isSendDisabled}
                      isSending={isSending}
                      placeholder={pendingNewSession ? "Type your first message..." : "Type a message..."}
                    />
                  </div>
                  {/* <VoiceButton
                    onTranscript={handleVoiceTranscript}
                    disabled={isSendDisabled}
                  /> */}
                </div>

                {/* Privacy and compliance text */}
                <div className="text-center mt-2">
                  <p className="text-xs text-rm-neutral-600">
                    AI answers can be inaccurate, use wisely.{' '}
                    <a
                      href={user?.consent?.privacyUrl || DEFAULT_URLS.PRIVACY_POLICY}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-rm-accent-600 hover:text-rm-accent-700 underline"
                    >
                      Privacy Notice
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Feedback section - Desktop Only */}
            {activeSessionId && (
              <>
                {/* Expandable Feedback Panel */}
                {isFeedbackExpanded && (
                  <div className="hidden md:flex border-l border-rm-neutral-600/20 flex-col h-full" style={{ width: '370px' }}>
                    <FeedbackPanel
                      sessionId={activeSessionId}
                      isVisible={true}
                    />
                  </div>
                )}

                {/* Feedback Toggle Button - Desktop Only */}
                <div className="hidden md:flex flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handlePanelToggle('feedback')}
                    className={`h-full rounded-none border-l border-rm-neutral-600/20 flex-col items-center justify-start pt-2 p-1 text-rm-neutral-600 hover:text-rm-primary-700 hover:bg-rm-bg-50 ${isFeedbackExpanded ? 'w-12' : 'w-12'
                      }`}
                  >
                    {isFeedbackExpanded ? (
                      <div className="flex flex-col items-center space-y-2">
                        <ChevronLeft className="w-4 h-4" />
                        <div className="writing-mode-vertical text-xs font-medium tracking-wide" style={{ writingMode: 'vertical-lr', textOrientation: 'mixed' }}>
                          Session Feedback
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center space-y-2">
                        <ChevronDown className="w-4 h-4" />
                        <div className="writing-mode-vertical text-xs font-medium tracking-wide" style={{ writingMode: 'vertical-lr', textOrientation: 'mixed' }}>
                          Session Feedback
                        </div>
                      </div>
                    )}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Active Runs Dialog - shown when 429 error occurs */}
      <ActiveRunsDialog
        isOpen={activeRunsDialogOpen}
        errorData={activeRunsError}
        onWait={handleActiveRunsWait}
        onCancel={handleActiveRunsCancel}
        onClose={handleActiveRunsWait}
        isCancelling={isCancellingRuns}
      />
    </div>
  );
};
